import { useEffect, useRef, useState } from 'react'
import {
  clearCachesAndReload,
  fetchDeployedVersion,
  getAppBuildId,
  webDeployNeedsReload,
} from './deployedVersion'
import './version-gate.css'

const POLL_MS = 45_000

function ReloadSplash() {
  return (
    <div
      className="w7-version-gate w7-version-gate--boot"
      data-testid="version-reloading"
    >
      <div
        className="w7-version-gate-splash"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span className="w7-version-gate-mark" aria-hidden="true">
          <img src="/brand/strix-logo.png" alt="" width={56} height={56} />
        </span>
        <div className="w7-version-gate-track" aria-hidden="true">
          <span className="w7-version-gate-bar" />
        </div>
        <p className="w7-version-gate-status">Loading new version</p>
      </div>
    </div>
  )
}

/**
 * Polls same-origin `/version.json` (baked every deploy). On mismatch, clears
 * caches and hard-reloads so open tabs pick up the new Vite asset map.
 *
 * Fail-open: missing/unreachable stamp never blocks the console (local dev).
 */
export function VersionGate({ children }: { children: React.ReactNode }) {
  const [reloading, setReloading] = useState(false)
  const reloadingRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    async function run() {
      if (reloadingRef.current) return

      const deployed = await fetchDeployedVersion()
      if (cancelled) return

      if (
        deployed &&
        webDeployNeedsReload(getAppBuildId(), deployed.buildId)
      ) {
        reloadingRef.current = true
        setReloading(true)
        await clearCachesAndReload()
      }
    }

    void run()

    const onFocus = () => {
      void run()
    }
    const interval = window.setInterval(() => {
      void run()
    }, POLL_MS)

    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      window.removeEventListener('focus', onFocus)
      window.clearInterval(interval)
    }
  }, [])

  if (reloading) return <ReloadSplash />
  return <>{children}</>
}
